import { useState, useMemo, useEffect } from "react";
import {
    IoCheckmark,
    IoClose,
    IoFilter,
    IoCheckmarkDone,
    IoTime,
    IoWarning,
    IoInformation,
    IoMailOpen,
    IoChevronDown,
    IoChevronForward,
    IoNotifications,
} from "react-icons/io5";
import { formatarDataBR, tempoRelativoBR } from "../../utils/date";
import { Loading } from "../shared/loading/Loading";
import Style from "./index.module.css";

const CATEGORIAS_NOTIFICACAO = {
    PRA: { label: "Categoria PRA", descricao: "Primeiro acesso ao sistema" },
    ACI: { label: "Categoria ACI", descricao: "Cadastro fiscal aprovado" },
    RCI: { label: "Categoria RCI", descricao: "Cadastro fiscal reprovado" },
    ACF: { label: "Categoria ACF", descricao: "Cadastro final aprovado" },
    RCF: { label: "Categoria RCF", descricao: "Cadastro final reprovado" },
    PEF: { label: "Categoria PEF", descricao: "Proposta enviada" },
    PRS: { label: "Categoria PRS", descricao: "Proposta respondida" },
    APR: { label: "Categoria APR", descricao: "Proposta aprovada" },
    RPR: { label: "Categoria RPR", descricao: "Proposta reprovada" },
    CFR: { label: "Categoria CFR", descricao: "Cadastro final realizado" },
    CRA: { label: "Categoria CRA", descricao: "Novo registro de avaliação" },
    NFR: { label: "Categoria NFR", descricao: "Novo cadastro de fornecedor" }
};

const getCategoriaInfo = (categoria) => {
    const codigo = String(categoria || "").trim().toUpperCase();

    return {
        codigo,
        label: CATEGORIAS_NOTIFICACAO[codigo]?.label || (codigo ? `Categoria ${codigo}` : "Outras notificações"),
        descricao: CATEGORIAS_NOTIFICACAO[codigo]?.descricao || "Atualizações gerais do sistema",
    };
};

const PopupNotificacoes = ({ show, isLoading, onChange, onClose, notificacoes = [], onMarcarComoLida }) => {
    const [filtroPeriodo, setFiltroPeriodo] = useState("ultima-semana");
    const [filtroCategoria, setFiltroCategoria] = useState("todas");
    const [mostrarApenasNaoLidas, setMostrarApenasNaoLidas] = useState(false);
    const [gruposMinimizados, setGruposMinimizados] = useState({});

    useEffect(() => {
        if (show) {
            setGruposMinimizados({});
        }
    }, [show]);

    useEffect(() => {
        const agora = new Date();
        let datIni, datFim;

        datFim = new Date(agora);
        datFim.setHours(23, 59, 59, 999);

        switch (filtroPeriodo) {
            case "hoje":
                datIni = new Date(agora);
                datIni.setHours(0, 0, 0, 0);
                break;
            case "ultima-semana":
                datIni = new Date(agora);
                datIni.setDate(agora.getDate() - 7);
                datIni.setHours(0, 0, 0, 0);
                break;
            case "ultimo-mes":
                datIni = new Date(agora);
                datIni.setDate(agora.getDate() - 30);
                datIni.setHours(0, 0, 0, 0);
                break;
        }

        if (
            onChange &&
            (!datIni || !datFim || datIni.getTime() !== datFim.getTime())
        ) {
            onChange(datIni, datFim);
        }
    }, [filtroPeriodo, onChange]);


    const notificacoesPorPeriodoStatus = useMemo(() => {
        const agora = new Date();
        const fim = new Date(agora);
        fim.setHours(23, 59, 59, 999);

        let inicio;
        switch (filtroPeriodo) {
            case "hoje":
                inicio = new Date(agora);
                inicio.setHours(0, 0, 0, 0);
                break;
            case "ultima-semana":
                inicio = new Date(agora);
                inicio.setDate(agora.getDate() - 7);
                inicio.setHours(0, 0, 0, 0);
                break;
            case "ultimo-mes":
                inicio = new Date(agora);
                inicio.setDate(agora.getDate() - 30);
                inicio.setHours(0, 0, 0, 0);
                break;
            default:
                inicio = new Date(0);
                break;
        }

        return notificacoes.filter((n) => {
            if (!n.datger) return false;

            const data = formatarDataBR(n.datger);
            if (!data) return false;

            return data >= inicio && data <= fim && (!mostrarApenasNaoLidas || n.notlid !== "1");
        });
    }, [notificacoes, filtroPeriodo, mostrarApenasNaoLidas]);

    const categoriasDisponiveis = useMemo(() => {
        const categorias = notificacoesPorPeriodoStatus.reduce((acc, notificacao) => {
            const categoria = getCategoriaInfo(notificacao.catnot);
            const key = categoria.codigo || "OUTRAS";

            if (!acc[key]) {
                acc[key] = categoria;
            }

            return acc;
        }, {});

        return Object.values(categorias).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    }, [notificacoesPorPeriodoStatus]);

    useEffect(() => {
        if (
            filtroCategoria !== "todas" &&
            !categoriasDisponiveis.some((categoria) => (categoria.codigo || "OUTRAS") === filtroCategoria)
        ) {
            setFiltroCategoria("todas");
        }
    }, [categoriasDisponiveis, filtroCategoria]);

    const notificacoesFiltradas = useMemo(() => (
        notificacoesPorPeriodoStatus.filter((notificacao) => {
            if (filtroCategoria === "todas") return true;

            const categoria = getCategoriaInfo(notificacao.catnot);
            return (categoria.codigo || "OUTRAS") === filtroCategoria;
        })
    ), [notificacoesPorPeriodoStatus, filtroCategoria]);

    const notificacoesAgrupadas = useMemo(() => {
        const grupos = notificacoesFiltradas.reduce((acc, notificacao) => {
            const categoria = getCategoriaInfo(notificacao.catnot);
            const key = categoria.codigo || "OUTRAS";

            if (!acc[key]) {
                acc[key] = {
                    ...categoria,
                    notificacoes: [],
                    naoLidas: 0,
                };
            }

            acc[key].notificacoes.push(notificacao);

            if (notificacao.notlid !== "1") {
                acc[key].naoLidas += 1;
            }

            return acc;
        }, {});

        return Object.values(grupos).sort((a, b) => {
            if (b.naoLidas !== a.naoLidas) return b.naoLidas - a.naoLidas;
            return a.label.localeCompare(b.label, "pt-BR");
        });
    }, [notificacoesFiltradas]);

    const alternarGrupoMinimizado = (codigo) => {
        setGruposMinimizados((grupos) => ({
            ...grupos,
            [codigo]: !(grupos[codigo] ?? true),
        }));
    };

    const getIcone = (tipo) => {
        switch (tipo) {
            case "ACI":
            case "ACF":
                return <IoCheckmark className={Style.iconeSucesso} />;
            case "RCI":
            case "RCF":
                return <IoWarning className={Style.iconeAviso} />;
            default:
                return <IoInformation className={Style.iconeInfo} />;
        }
    };

    const handleMarcarComoLida = (id, e) => {
        e.stopPropagation();
        onMarcarComoLida([id]);
    };

    const marcarTodasComoLidas = (e) => {
        e.stopPropagation();
        const arqs = notificacoesFiltradas.filter((n) => n.notlid !== "1").map((n) => n.arq487);
        if (arqs.length > 0) onMarcarComoLida(arqs);
    };

    if (!show) return null;

    return (
        <div className={Style.popupNotificacoes}>
            <div className={Style.popupNotificacoesHeader}>
                <div className={Style.popupNotificacoesTitulo}>
                    <IoNotifications />
                    <h4>Notificações</h4>
                </div>
                <div className={Style.popupNotificacoesActions}>
                    <button
                        onClick={() => setMostrarApenasNaoLidas(!mostrarApenasNaoLidas)}
                        className={`${Style.filtroButton} ${mostrarApenasNaoLidas ? Style.ativo : ""}`}
                        title={mostrarApenasNaoLidas ? "Mostrar todas" : "Mostrar apenas não lidas"}
                    >
                        <IoFilter size={16} />
                    </button>

                    {notificacoesFiltradas.some((n) => n.notlid !== "1") && (
                        <button
                            onClick={marcarTodasComoLidas}
                            className={Style.marcarTodasButton}
                            title="Marcar todas como lidas"
                        >
                            <IoCheckmarkDone size={16} />
                        </button>
                    )}

                    <button onClick={onClose} className={Style.fecharButton} title="Fechar">
                        <IoClose size={18} />
                    </button>
                </div>
            </div>

            <div className={Style.popupNotificacoesFiltros}>
                <div className={Style.filtroGroup}>
                    <label htmlFor="filtroPeriodo" className={Style.filtroLabel}>
                        Período:
                    </label>
                    <div className={Style.selectWrapper}>
                        <select
                            id="filtroPeriodo"
                            value={filtroPeriodo}
                            onChange={(e) => setFiltroPeriodo(e.target.value)}
                            className={Style.filtroSelect}
                        >
                            <option value="hoje">Hoje</option>
                            <option value="ultima-semana">Última semana</option>
                            <option value="ultimo-mes">Último mês</option>
                        </select>
                        <IoChevronDown className={Style.selectArrow} />
                    </div>
                </div>

                <div className={Style.filtroGroup}>
                    <label htmlFor="filtroCategoria" className={Style.filtroLabel}>
                        Categoria:
                    </label>
                    <div className={Style.selectWrapper}>
                        <select
                            id="filtroCategoria"
                            value={filtroCategoria}
                            onChange={(e) => setFiltroCategoria(e.target.value)}
                            className={Style.filtroSelect}
                        >
                            <option value="todas">Todas</option>
                            {categoriasDisponiveis.map((categoria) => (
                                <option key={categoria.codigo || "OUTRAS"} value={categoria.codigo || "OUTRAS"}>
                                    {categoria.label}
                                </option>
                            ))}
                        </select>
                        <IoChevronDown className={Style.selectArrow} />
                    </div>
                </div>

                <div className={Style.filtroStatus}>
                    <span className={Style.contador}>
                        {notificacoesFiltradas.length}{" "}
                        {notificacoesFiltradas.length === 1 ? "item" : "itens"}
                    </span>
                    {filtroCategoria !== "todas" && (
                        <span className={Style.filtroAtivo}>
                            {categoriasDisponiveis.find((categoria) => (categoria.codigo || "OUTRAS") === filtroCategoria)?.label || filtroCategoria}
                        </span>
                    )}
                    {mostrarApenasNaoLidas && (
                        <span className={Style.filtroAtivo}>Apenas não lidas</span>
                    )}
                </div>
            </div>

            <div className={Style.popupNotificacoesLista}>
                {isLoading && (
                    <Loading text="Carregando notificações..." />
                )}

                {notificacoesAgrupadas.length > 0 ? (
                    <div className={Style.notificacaoGrupos}>
                        {notificacoesAgrupadas.map((grupo) => {
                            const grupoKey = grupo.codigo || "OUTRAS";
                            const grupoMinimizado = gruposMinimizados[grupoKey] ?? true;

                            return (
                                <section key={grupoKey} className={Style.notificacaoGrupo}>
                                    <button
                                        type="button"
                                        className={Style.notificacaoGrupoHeader}
                                        onClick={() => alternarGrupoMinimizado(grupoKey)}
                                        aria-expanded={!grupoMinimizado}
                                        title={grupoMinimizado ? "Expandir categoria" : "Minimizar categoria"}
                                    >
                                        <div>
                                            <h5>{grupo.label}</h5>
                                            <span>{grupo.descricao}</span>
                                        </div>
                                        <div className={Style.notificacaoGrupoBadges}>
                                            {grupo.naoLidas > 0 && (
                                                <span className={Style.grupoNaoLidas}>
                                                    {grupo.naoLidas} nova{grupo.naoLidas > 1 ? "s" : ""}
                                                </span>
                                            )}
                                            <span className={Style.grupoTotal}>
                                                {grupo.notificacoes.length}
                                            </span>
                                            <span className={Style.grupoToggle}>
                                                {grupoMinimizado ? <IoChevronForward /> : <IoChevronDown />}
                                            </span>
                                        </div>
                                    </button>

                                    {!grupoMinimizado && (
                                        <ul>
                                            {grupo.notificacoes.map((n) => (
                                                <li
                                                    key={n.arq487}
                                                    className={`${Style.notificacao} ${n.notlid !== "1" ? Style.naoLida : ""} ${n.tipo ? Style[`tipo${n.tipo}`] : ""
                                                        }`}
                                                >
                                                    <div className={Style.notificacaoIcone}>
                                                        {n.icone || getIcone(n.catnot)}
                                                    </div>
                                                    <div className={Style.notificacaoConteudo}>
                                                        <div className={Style.notificacaoInfo}>
                                                            <div className={Style.notificacaoMensagem}>{n.desnot}</div>
                                                            {n.obsnot && (
                                                                <div className={Style.notificacaoDescricao}>{n.obsnot}</div>
                                                            )}
                                                            <div className={Style.notificacaoMeta}>
                                                                <span className={Style.notificacaoTempo}>
                                                                    <IoTime size={12} /> {tempoRelativoBR(n.datger)}
                                                                </span>
                                                                {n.catnot && (
                                                                    <span className={Style.notificacaoCategoria}>{n.catnot}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={Style.botaoContainer}>
                                                        {n.notlid !== "1" && (
                                                            <button
                                                                onClick={(e) => handleMarcarComoLida(n.arq487, e)}
                                                                className={Style.botaoMarcarLida}
                                                                title="Marcar como lida"
                                                            >
                                                                <IoMailOpen style={{ color: "#fff" }} size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                ) : (
                    <div className={Style.popupNotificacoesVazio}>
                        <div className={Style.popupNotificacoesVazioIcone}>
                            <IoCheckmarkDone size={48} />
                        </div>
                        <h5>Nenhuma notificação</h5>
                        <p style={{ fontSize: "14px" }}>
                            {mostrarApenasNaoLidas
                                ? "Não há notificações não lidas no período selecionado"
                                : "Você está atualizado com todas as novidades!"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export { PopupNotificacoes };
